/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.20.0
 */

import { Fragment } from 'react';

import { FormFieldStyled } from '@components/common/FormStyled/FormStyled';
import { EInputKind } from '@components/common/Input/Input';
import type { IOptionItemProps } from '@components/common/Select/Select';
import type { IFormFieldProps } from '@modules/form/components/FormField';
import FormFieldCheckbox from '@modules/form/components/FormFieldCheckbox';
import FormFieldDate from '@modules/form/components/FormFieldDate';
import FormFieldInput from '@modules/form/components/FormFieldInput';

import {
    ECreateInquiryType,
    ECreateInquiryFieldType as Type,
    ECreateInquiryFieldVisibility as Visibility,
} from '../../constants';
import type { ICreateInquiryAttribute, TCreateInquiryAttributeValue } from '../../types';
import CreateInquiryFieldLabel from '../CreateInquiryFieldLabel';
import { InquiryFieldTextStyled } from '../styled/CreateInquiryAtrributeStyled';
import { CreateInquiryFormFieldSelect } from './CreateInquiryFormFieldSelect';

interface ICreateInquiryCustomAttributes {
    attribute: ICreateInquiryAttribute;
    dictionary: IOptionItemProps[];
    id?: string;
    topicCode: string;
    type: ECreateInquiryType;
    masterAttributes: TCreateInquiryAttributeValue[];
    onAttributeChange: (attribute: ICreateInquiryAttribute, newValue: any) => void;
}

const CreateInquiryCustomAttribute = ({
    attribute,
    dictionary,
    onAttributeChange,
    ...dictionaryParams
}: ICreateInquiryCustomAttributes) => {
    const dataName = `inquiry-field-attribute-${attribute.code}`;
    const props: IFormFieldProps = { dataName, name: attribute.code.toString() };

    const onAttributeChangeCallback = (newValue: any) => {
        if (attribute.declaration.isMaster) {
            onAttributeChange(attribute, newValue);
        }
    };

    // Некоторые атрибуты являются текстовыми подсказками
    if (attribute.declaration.type === Type.dbQuery || attribute.declaration.accessibility === Visibility.readOnly) {
        const value = attribute.declaration.stringValue || attribute.declaration.textValue;

        if (value) {
            return (
                <FormFieldStyled kind={EInputKind.normal}>
                    <InquiryFieldTextStyled dangerouslySetInnerHTML={{ __html: value }} />
                </FormFieldStyled>
            );
        }
    }

    const errorKey = !!attribute.declaration.multiSelect
        ? `${attribute.code.toString()}`
        : `${attribute.code.toString()}.value`;

    return (
        <Fragment key={dataName}>
            {attribute.name && attribute.declaration.type !== Type.bool && (
                <CreateInquiryFieldLabel
                    name={attribute.name}
                    prompt={attribute.declaration.prompt}
                    required={attribute.declaration.accessibility === Visibility.mandatory}
                />
            )}
            {
                {
                    [Type.string]: <FormFieldInput {...props} errorKey={errorKey} />,
                    [Type.number]: <FormFieldInput {...props} />,
                    [Type.date]: <FormFieldDate onSelectDate={onAttributeChangeCallback} {...props} />,
                    [Type.bool]: (
                        <FormFieldCheckbox {...props} label={attribute.name} onChange={onAttributeChangeCallback} />
                    ),
                    [Type.dictionary]: (
                        <CreateInquiryFormFieldSelect
                            {...props}
                            dictionary={dictionary}
                            errorKey={errorKey}
                            isMultiSelect={!!attribute.declaration.multiSelect}
                            params={{
                                attributeId: attribute.id,
                                ...dictionaryParams,
                            }}
                            onSelect={onAttributeChangeCallback}
                        />
                    ),
                    [Type.dbQuery]: null,
                }[attribute.declaration.type]
            }
        </Fragment>
    );
};

export default CreateInquiryCustomAttribute;
